import React, { useCallback } from 'react';
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { debounce } from "lodash";

interface TemplatePrivacySettingsProps {
  templateId?: string;
  includePrivacyNotice: boolean;
  privacyNoticePosition: string;
  customPrivacyText: string;
  defaultPrivacyText: string;
  onIncludePrivacyNoticeChange: (value: boolean) => void;
  onPrivacyNoticePositionChange: (value: string) => void;
  onCustomPrivacyTextChange: (value: string) => void;
}

export const TemplatePrivacySettings = ({
  templateId,
  includePrivacyNotice,
  privacyNoticePosition,
  customPrivacyText,
  defaultPrivacyText,
  onIncludePrivacyNoticeChange,
  onPrivacyNoticePositionChange,
  onCustomPrivacyTextChange,
}: TemplatePrivacySettingsProps) => {
  const privacyText = customPrivacyText || defaultPrivacyText;

  const updatePrivacyMutation = useMutation({
    mutationFn: async (newText: string) => {
      if (!templateId) return;
      
      const { error } = await supabase
        .from('newsletter_templates')
        .update({
          custom_privacy_text: newText,
        })
        .eq('id', templateId);

      if (error) throw error;
    },
    onError: (error) => {
      console.error('Error saving privacy text:', error);
      toast.error('Failed to save privacy text');
    }
  });

  // Debounced save function
  const debouncedSave = useCallback(
    debounce((text: string) => {
      if (templateId) {
        updatePrivacyMutation.mutate(text);
      }
    }, 1000),
    [templateId]
  );

  const handleTextChange = (value: string) => {
    onCustomPrivacyTextChange(value);
    debouncedSave(value);
  };

  const renderPreview = () => {
    if (!includePrivacyNotice) return null;

    return (
      <div className="mt-4 space-y-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Eye className="h-4 w-4" />
          <span>Preview</span>
        </div>
        <Card className="p-4 bg-muted/50">
          <div className="prose dark:prose-invert max-w-none">
            {privacyNoticePosition === 'header' && (
              <div className="mb-4 text-sm text-muted-foreground border-b pb-2">
                {privacyText}
              </div>
            )}
            <div className="min-h-[100px] bg-muted/30 flex items-center justify-center text-muted-foreground">
              Newsletter Content Area
            </div>
            {privacyNoticePosition === 'footer' && (
              <div className="mt-4 text-sm text-muted-foreground border-t pt-2">
                {privacyText}
                <div className="mt-2 text-xs">
                  <Link to="/unsubscribe" className="text-primary hover:underline">Unsubscribe</Link> | 
                  <Link to="/privacy" className="text-primary hover:underline ml-2">Privacy Policy</Link> | 
                  <Link to="/terms" className="text-primary hover:underline ml-2">Terms of Service</Link>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Label htmlFor="privacy-notice" className="flex flex-col">
          <span>Include Privacy Notice</span>
          <span className="font-normal text-sm text-muted-foreground">
            Add a privacy notice to your newsletter
          </span>
        </Label>
        <Switch
          id="privacy-notice"
          checked={includePrivacyNotice}
          onCheckedChange={onIncludePrivacyNoticeChange}
        />
      </div>

      {includePrivacyNotice && (
        <>
          <div className="space-y-2">
            <Label htmlFor="notice-position">Notice Position</Label>
            <Select
              value={privacyNoticePosition}
              onValueChange={onPrivacyNoticePositionChange}
            >
              <SelectTrigger id="notice-position">
                <SelectValue placeholder="Select position" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="header">Header</SelectItem>
                <SelectItem value="footer">Footer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="custom-text">
              Custom Privacy Text
              <span className="text-sm font-normal text-muted-foreground ml-2">
                (Leave blank to use default)
              </span>
            </Label>
            <Textarea
              id="custom-text"
              placeholder="Enter custom privacy notice text..."
              value={customPrivacyText}
              onChange={(e) => handleTextChange(e.target.value)}
              className="min-h-[100px] resize-y"
            />
            <ScrollArea className="h-[100px] w-full rounded-md border">
              <div className="p-4">
                <p className="text-sm text-muted-foreground">Default Text:</p>
                <p className="text-sm mt-2">{defaultPrivacyText}</p>
              </div>
            </ScrollArea>
          </div>

          {renderPreview()}
        </>
      )}
    </div>
  );
};